import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import Loader from "../components/Loader";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity } from "../context/EffectiveIdentityContext";
import { FaPeopleCarry, FaEnvelope, FaPhone, FaMapMarkerAlt, FaTag, FaSearch, FaUsers } from "react-icons/fa";
import { tolerantId } from "../utils/firestoreHelpers";
import { esPermisoDenegado } from "../utils/consultasEdificio";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppCard, AppEmptyState, IconBox, Badge, Avatar, SectionLabel } from "../components/app";
import { appColors, appType, appRadius, appSpacing, appShadows } from "../utils/appTheme";

const Proveedores = ({ setSelected, goBack }) => {
  const { t } = useTranslation("proveedores");
  const { selectedBuilding } = useBuilding();
  const { email: effEmail } = useEffectiveIdentity();
  const isMobile = useIsMobile();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  // Las reglas negaron el directorio (la demo no lee el catálogo de proveedores):
  // se muestra un estado vacío que lo explica en vez de una lista vacía muda.
  const [sinAcceso, setSinAcceso] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedBuilding || !effEmail) return;

    const fetchProveedores = async () => {
      setLoading(true);
      setSinAcceso(false);
      try {
        // Query solo por buildingId (número o string) para evitar necesidad de
        // indice compuesto; la regla mira ese campo. showToClient se filtra
        // client-side.
        const supplierBuildingQuery = query(
          collection(db, "supplierbuilding"),
          where("buildingId", "in", tolerantId(selectedBuilding.buildingid))
        );
        const supplierBuildingSnap = await getDocs(supplierBuildingQuery);

        // Filtrar client-side: solo los que tienen showToClient !== false
        const visibleRelations = supplierBuildingSnap.docs
          .map(doc => doc.data())
          .filter(rel => rel.showToClient !== false);

        if (visibleRelations.length === 0) {
          setProveedores([]);
          setLoading(false);
          return;
        }

        const rawSupplierIds = visibleRelations.map(rel => rel.supplierId);

        // Generar variantes Number/String de cada supplierId para tolerar tipos mixtos
        const supplierIdVariants = [];
        const seen = new Set();
        rawSupplierIds.forEach(id => {
          tolerantId(id).forEach(v => {
            const key = `${typeof v}:${v}`;
            if (!seen.has(key)) { seen.add(key); supplierIdVariants.push(v); }
          });
        });

        // Descargar suppliers en batches de 10 (limite Firestore para "in")
        const proveedoresData = [];
        for (let i = 0; i < supplierIdVariants.length; i += 10) {
          const batch = supplierIdVariants.slice(i, i + 10);
          const batchQuery = query(
            collection(db, "supplier"),
            where("supplierId", "in", batch)
          );
          const batchSnap = await getDocs(batchQuery);
          batchSnap.docs.forEach(doc => {
            // Evitar duplicados si se resuelve por ambas variantes
            if (!proveedoresData.some(p => p.id === doc.id)) {
              proveedoresData.push({ id: doc.id, ...doc.data() });
            }
          });
        }
        
        setProveedores(proveedoresData);
      } catch (error) {
        if (esPermisoDenegado(error)) {
          console.warn("Directorio de proveedores no disponible:", error.code);
          setSinAcceso(true);
        } else {
          console.error("Error al cargar proveedores:", error);
        }
        setProveedores([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProveedores();
  }, [selectedBuilding, effEmail]);

  // ─── Contenido mobile: estilo app "Proveedores" (screen 12) ─────────────────
  if (isMobile) {
    const q = search.trim().toLowerCase();
    const visibles = q
      ? proveedores.filter((p) =>
          [p.name, p.type, p.email, p.telephone, p.address]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(q))
        )
      : proveedores;

    // Derivados de presentación (read-only) sobre el estado ya calculado.
    const total = proveedores.length;
    const sinAccesoTitulo = t("unavailable.title", { defaultValue: "Directorio no disponible" });
    const sinAccesoTexto = t("unavailable.description", {
      defaultValue: "Esta cuenta no tiene acceso al directorio de proveedores del edificio.",
    });
    const iniciales = (nombre) =>
      String(nombre || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "?";

    // Fila de contacto: micro-tile con IconBox + label uppercase + valor.
    const ContactRow = ({ icon, label, value, href }) => {
      const inner = (
        <>
          <IconBox icon={icon} size={34} radius={appRadius.sm} />
          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
            <span style={{ ...appType.statLabel, color: appColors.ink3 }}>{label}</span>
            <span style={{ ...appType.body, fontSize: 13.5, overflowWrap: "anywhere" }}>{value}</span>
          </span>
        </>
      );
      const boxStyle = {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: appColors.surface2,
        borderRadius: appRadius.sm,
        minWidth: 0,
        textDecoration: "none",
        color: "inherit",
      };
      return href ? (
        <a href={href} style={boxStyle}>{inner}</a>
      ) : (
        <div style={boxStyle}>{inner}</div>
      );
    };

    return (
      <AppScreen
        header={
          <AppHeader
            title={t("title")}
            subtitle={t("subtitleMobile")}
            onBack={goBack}
          />
        }
      >
        {/* Barra de búsqueda */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            background: appColors.surface,
            border: `1px solid ${appColors.line}`,
            borderRadius: appRadius.button,
            boxShadow: appShadows.soft,
            marginBottom: appSpacing.section,
          }}
        >
          <IconBox icon={<FaSearch />} size={32} radius={appRadius.sm} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("searchPlaceholder")}
            aria-label={t("searchAria")}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              background: "transparent",
              ...appType.body,
              fontSize: 14,
              color: appColors.ink,
            }}
          />
        </div>

        {!selectedBuilding?.buildingid ? (
          <AppEmptyState
            icon={<FaUsers />}
            title={t("selectBuilding.title")}
            description={t("selectBuilding.description")}
          />
        ) : loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <Loader />
          </div>
        ) : sinAcceso ? (
          <AppEmptyState icon={<FaUsers />} title={sinAccesoTitulo} description={sinAccesoTexto} />
        ) : visibles.length === 0 ? (
          <AppEmptyState
            icon={<FaUsers />}
            title={q ? t("noResults.title") : t("empty.title")}
            description={
              q
                ? t("noResults.description")
                : t("empty.description")
            }
          />
        ) : (
          <>
            {/* Etiqueta de sección con conteo */}
            <SectionLabel
              style={{ marginBottom: 12 }}
              action={
                q && total
                  ? { label: t("shownOfTotal", { shown: visibles.length, total }), onClick: () => {} }
                  : undefined
              }
            >
              {q ? t("results") : t("directory", { count: total })}
            </SectionLabel>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {visibles.map((prov) => (
                <AppCard key={prov.id} accent="blue" padding={16}>
                  {/* Cabecera: avatar/imagen + nombre + rubro como Badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    {prov.image ? (
                      <Avatar photoURL={prov.image} initials={iniciales(prov.name)} size={44} />
                    ) : (
                      <Avatar initials={iniciales(prov.name)} size={44} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>
                        {prov.name}
                      </div>
                      {prov.type && (
                        <div style={{ marginTop: 6 }}>
                          <Badge tone="blue">
                            <FaTag size={9} /> {prov.type}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contactos en micro-tiles */}
                  {(prov.telephone || prov.email || prov.address) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                      {prov.telephone && (
                        <ContactRow
                          icon={<FaPhone />}
                          label={t("phone")}
                          value={prov.telephone}
                          href={`tel:${String(prov.telephone).replace(/\s+/g, "")}`}
                        />
                      )}
                      {prov.email && (
                        <ContactRow
                          icon={<FaEnvelope />}
                          label={t("email")}
                          value={prov.email}
                          href={`mailto:${prov.email}`}
                        />
                      )}
                      {prov.address && (
                        <ContactRow
                          icon={<FaMapMarkerAlt />}
                          label={t("address")}
                          value={prov.address}
                        />
                      )}
                    </div>
                  )}
                </AppCard>
              ))}
            </div>
          </>
        )}
      </AppScreen>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <FaPeopleCarry />
        </div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{t("title")}</h1>
          <p style={styles.subtitle}>{t("subtitle")}</p>
        </div>
      </div>

      {!selectedBuilding?.buildingid ? (
        <div style={styles.emptyState}>
          <FaPeopleCarry style={styles.emptyIcon} />
          <p style={styles.emptyText}>{t("selectBuildingDesktop")}</p>
        </div>
      ) : loading ? (
        <div style={styles.loaderContainer}><Loader /></div>
      ) : sinAcceso ? (
        <div role="status" style={styles.emptyState}>
          <FaPeopleCarry style={styles.emptyIcon} />
          <p style={{ ...styles.emptyText, marginBottom: 4 }}>
            {t("unavailable.title", { defaultValue: "Directorio no disponible" })}
          </p>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
            {t("unavailable.description", {
              defaultValue: "Esta cuenta no tiene acceso al directorio de proveedores del edificio.",
            })}
          </p>
        </div>
      ) : proveedores.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 24px", color: "#6b7280" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔧</div>
          <p style={{ fontSize: 16, fontWeight: 400, marginBottom: 4 }}>{t("emptyDesktop.title")}</p>
          <p style={{ fontSize: 13 }}>{t("emptyDesktop.description")}</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {proveedores.map((prov) => (
            <div key={prov.id} style={styles.card}>
              {prov.image && (
                <img src={prov.image} alt={prov.name} style={styles.image} loading="lazy" />
              )}
              <div style={styles.cardContent}>
                <h3 style={styles.name}>{prov.name}</h3>
                <div style={styles.detailRow}>
                  <FaTag style={styles.detailIcon} />
                  <span style={styles.detailText}>{prov.type}</span>
                </div>
                <div style={styles.detailRow}>
                  <FaEnvelope style={styles.detailIcon} />
                  <span style={styles.detailText}>{prov.email}</span>
                </div>
                <div style={styles.detailRow}>
                  <FaPhone style={styles.detailIcon} />
                  <span style={styles.detailText}>{prov.telephone}</span>
                </div>
                <div style={styles.detailRow}>
                  <FaMapMarkerAlt style={styles.detailIcon} />
                  <span style={styles.detailText}>{prov.address}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: "24px",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  header: {
    background: "white",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    backgroundColor: "#0080ff",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px"
  },
  headerInfo: {
    flex: 1
  },
  title: {
    fontSize: "20px",
    fontWeight: 400,
    margin: 0,
    color: "#0080ff"
  },
  subtitle: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "2px 0 0 0",
    fontWeight: 400
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "48px"
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "64px 32px",
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)"
  },
  emptyIcon: {
    fontSize: "48px",
    color: "#0080ff",
    opacity: 0.5,
    marginBottom: "16px"
  },
  emptyText: {
    fontSize: "16px",
    color: "#6b7280",
    margin: 0
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "20px"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    transition: "all 0.2s ease"
  },
  image: {
    width: "100%",
    height: "160px",
    objectFit: "cover"
  },
  cardContent: {
    padding: "20px"
  },
  name: {
    fontSize: "18px",
    fontWeight: 400,
    color: "#1a1a1a",
    marginBottom: "16px",
    margin: "0 0 16px 0"
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px"
  },
  detailIcon: {
    fontSize: "14px",
    color: "#0080ff",
    width: "16px",
    flexShrink: 0
  },
  detailText: {
    fontSize: "14px",
    color: "#4b5563"
  }
};

export default Proveedores;
